import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { AppHeader } from '@/components/layout/AppHeader';

// Helper function to get descriptive label from slider value
function getLabel(value: number, type: 'social' | 'weekend' | 'pace' | 'communication'): string {
  const labels = {
    social: {
      low: 'Introvert',
      mediumLow: 'Moderate Introvert',
      mediumHigh: 'Moderate Extrovert',
      high: 'Extrovert',
    },
    weekend: {
      low: 'Homebody',
      mediumLow: 'Mostly Relaxed',
      mediumHigh: 'Mostly Active',
      high: 'Always Out',
    },
    pace: {
      low: 'Very Slow',
      mediumLow: 'Slow & Steady',
      mediumHigh: 'Moderate',
      high: 'Fast-Paced',
    },
    communication: {
      low: 'Brief & Casual',
      mediumLow: 'Balanced',
      mediumHigh: 'Conversational',
      high: 'Deep & Chatty',
    },
  };

  const categoryLabels = labels[type];
  if (value <= 25) return categoryLabels.low;
  if (value <= 50) return categoryLabels.mediumLow;
  if (value <= 75) return categoryLabels.mediumHigh;
  return categoryLabels.high;
}

export const ProfileLifestylePage: React.FC = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [socialEnergy, setSocialEnergy] = useState(50);
  const [weekendEnergy, setWeekendEnergy] = useState(50);
  const [datingPace, setDatingPace] = useState(50);
  const [communicationStyle, setCommunicationStyle] = useState(50);

  // Save preferences mutation
  const savePreferencesMutation = useMutation({
    mutationFn: async (data: {
      lifestylePreferences: {
        socialLevel: number;
        weekendEnergy: number;
        datingPace: number;
        communicationStyle: number;
      };
    }) => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/profiles/me`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to save lifestyle preferences');
      return response.json();
    },
  });

  const handleSaveAndContinue = async () => {
    try {
      await savePreferencesMutation.mutateAsync({
        lifestylePreferences: {
          socialLevel: socialEnergy,
          weekendEnergy,
          datingPace,
          communicationStyle,
        },
      });

      toast.success('Lifestyle preferences saved!');
      navigate('/profile/preview');

    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save preferences. Please try again.');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <AppHeader />
      <div className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-20">
        <div className="w-full max-w-2xl">
          <Card className="shadow-xl bg-brand-surface border-brand-border">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-white">
              Your Lifestyle
            </CardTitle>
            <CardDescription className="text-lg mt-2 text-brand-muted">
              Help us understand your daily rhythms and social energy
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-8">
            {/* Social Energy Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="socialEnergy" className="text-base font-semibold text-white">
                  Social Energy
                </Label>
                <span className="text-sm font-medium text-brand-primary">
                  {getLabel(socialEnergy, 'social')}
                </span>
              </div>
              <Slider
                id="socialEnergy"
                min={0}
                max={100}
                step={1}
                value={[socialEnergy]}
                onValueChange={(value) => setSocialEnergy(value[0])}
                className="w-full"
              />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Introvert</span>
                <span>Extrovert</span>
              </div>
            </div>

            {/* Weekend Energy Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="weekendEnergy" className="text-base font-semibold text-white">
                  Weekend Energy
                </Label>
                <span className="text-sm font-medium text-brand-primary">
                  {getLabel(weekendEnergy, 'weekend')}
                </span>
              </div>
              <Slider
                id="weekendEnergy"
                min={0}
                max={100}
                step={1}
                value={[weekendEnergy]}
                onValueChange={(value) => setWeekendEnergy(value[0])}
                className="w-full"
              />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Chill at Home</span>
                <span>Out & About</span>
              </div>
            </div>

            {/* Dating Pace Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="datingPace" className="text-base font-semibold text-white">
                  Dating Pace
                </Label>
                <span className="text-sm font-medium text-brand-primary">
                  {getLabel(datingPace, 'pace')}
                </span>
              </div>
              <Slider
                id="datingPace"
                min={0}
                max={100}
                step={1}
                value={[datingPace]}
                onValueChange={(value) => setDatingPace(value[0])}
                className="w-full"
              />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Slow & Steady</span>
                <span>Fast & Exciting</span>
              </div>
            </div>

            {/* Communication Style Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="communicationStyle" className="text-base font-semibold text-white">
                  Communication Style
                </Label>
                <span className="text-sm font-medium text-brand-primary">
                  {getLabel(communicationStyle, 'communication')}
                </span>
              </div>
              <Slider
                id="communicationStyle"
                min={0}
                max={100}
                step={1}
                value={[communicationStyle]}
                onValueChange={(value) => setCommunicationStyle(value[0])}
                className="w-full"
              />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Brief & Casual</span>
                <span>Deep & Chatty</span>
              </div>
            </div>

            {/* Helper Text */}
            <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <p className="text-sm text-purple-200">
                <strong>Matching Tip:</strong> These preferences help us find people with compatible lifestyles and energy levels.
                There's no right or wrong answer—just be honest!
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-4">
              <Button
                onClick={handleBack}
                variant="ghost"
                className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full px-6"
                disabled={savePreferencesMutation.isPending}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>

              <Button
                onClick={handleSaveAndContinue}
                disabled={savePreferencesMutation.isPending}
                className="bg-brand-primary hover:bg-brand-primary/90 hover:scale-105 text-white flex items-center gap-2 px-8 rounded-full shadow-lg shadow-brand-primary/20 transition-transform"
              >
                {savePreferencesMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Save & Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Helper Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[#1E3A5F]/80">
            You can always update these preferences later in your profile settings
          </p>
          </div>
        </div>
      </div>
    </>
  );
};
